import { createOwnedDbHelpers } from "@9amhealth/shared";
import Dexie from "dexie";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import type {
  CachedAnsweredQuestionnaires,
  CachedConversation,
  CachedFile,
  CachedJourneyContent,
  CachedJourneyStatuses,
  CachedMessage,
  CachedTask,
  JourneyMeta,
  SyncMeta
} from "./dbTypes";
import { clearUserJourneyHydration } from "./UserJourneyHydrationCache";
import { clearLearnPageViewConfigHydration } from "./LearnPageViewConfigHydrationCache";

const DB_OWNER_KEY = "user-app-db-owner";

class UserAppDb extends Dexie {
  conversations!: Dexie.Table<CachedConversation, string>;
  messages!: Dexie.Table<CachedMessage, string>;
  syncMeta!: Dexie.Table<SyncMeta, string>;
  prompts!: Dexie.Table<unknown, string>;
  cachedFiles!: Dexie.Table<CachedFile, string>;
  tasks!: Dexie.Table<CachedTask, string>;
  journeyContent!: Dexie.Table<CachedJourneyContent, string>;
  journeyStatuses!: Dexie.Table<CachedJourneyStatuses, string>;
  answeredQuestionnaires!: Dexie.Table<CachedAnsweredQuestionnaires, string>;
  meta!: Dexie.Table<JourneyMeta, string>;

  constructor() {
    super("LeapChatDb");

    this.version(1).stores({
      conversations: "id, updatedAt",
      messages: "id, conversationId, createdAt, [conversationId+createdAt]",
      syncMeta: "key"
    });

    this.version(2).stores({
      prompts: "id, conversationId, messageId"
    });

    this.version(3).stores({
      cachedFiles: "id, cachedAt"
    });

    this.version(4).stores({
      tasks: "id, program",
      journeyContent: "key, journey",
      journeyStatuses: "key",
      answeredQuestionnaires: "key",
      meta: "key"
    });
  }
}

let instance: UserAppDb | null = null;

export const getDb = (): UserAppDb => {
  instance ??= new UserAppDb();
  return instance;
};

const helpers = createOwnedDbHelpers(getDb(), {
  ownerKey: DB_OWNER_KEY,
  getCurrentOwnerId: () => StorageController.activeUserId
});

export const { ensureOwnership } = helpers;
export const { invalidateOwnership } = helpers;
export const { runForOwner } = helpers;

export const clearDb = async (): Promise<void> => {
  await helpers.clearAll();
  clearUserJourneyHydration();
  clearLearnPageViewConfigHydration();
};
